import { useState } from 'react';
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import Toast from 'react-native-toast-message';

import Text from './Text';
import BrandedButton from '@/components/buttons/BrandedButton';
import { useColor } from '@/hooks/useColor';
import { firebaseImage, safeJsonParseArray, ThumbSize } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';

// ─── Fixed pixel dimensions used by both display and canvas draw ──────────────
const CARD_WIDTH = 320;
const QR_SIZE = 170;
const LOGO_SIZE = 56;
const PAD = 20;

interface ShareQrCardProps {
  npoName: string;
  thumbnail?: string;
  qrUrl: string;
  shortLink: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const roundedRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

// ─── Image helpers ────────────────────────────────────────────────────────────

/**
 * Loads a canvas-drawable HTMLImageElement from any CORS-accessible URL.
 * Firebase Storage URLs are skipped (browser CORS blocks all JS reads from
 * Firebase Storage without explicit bucket CORS configuration — img tags are
 * exempt, but Canvas drawImage is not).
 */
const loadCanvasImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (err) => {
      if (src.includes('firebasestorage.googleapis.com')) {
        reject(new Error('firebase-cors'));
      } else {
        reject(err);
      }
    };
    img.src = src;
  });

const drawCircleImage = (ctx: CanvasRenderingContext2D, img: HTMLImageElement, cx: number, cy: number, r: number) => {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  ctx.drawImage(img, cx - r, cy - r, r * 2, r * 2);
  ctx.restore();
};

/** Draws a styled initials badge when the logo image is unavailable. */
const drawInitialsPlaceholder = (
  ctx: CanvasRenderingContext2D,
  name: string,
  cx: number,
  cy: number,
  r: number,
  font: string
) => {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  // Circle fill
  ctx.fillStyle = '#fffceeff';
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();

  // Circle border
  ctx.strokeStyle = '#fef5c7ff';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(cx, cy, r - 0.75, 0, Math.PI * 2);
  ctx.stroke();

  // Initials text
  ctx.fillStyle = '#e5bd46ff';
  ctx.font = `700 ${Math.round(r * 0.72)}px ${font}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(initials, cx, cy + 1);
};

// ─── Component ────────────────────────────────────────────────────────────────

const ShareQrCardWeb = ({ npoName, thumbnail, qrUrl, shortLink }: ShareQrCardProps) => {
  const colors = useColor();
  const [isCapturing, setIsCapturing] = useState(false);

  const npoImage =
    thumbnail && thumbnail !== '[]'
      ? firebaseImage(safeJsonParseArray(thumbnail ?? '[]')?.at(0), ThumbSize.Small)
      : undefined;

  const onCopyLink = async () => {
    await Clipboard.setStringAsync(shortLink);
    Toast.show({
      type: 'success',
      text1: i18n.t('mix.copied.title'),
      text2: i18n.t('mix.copied.link'),
      position: 'bottom'
    });
  };

  // ── Canvas-based download — 100% zoom-independent ──────────────────────────
  const onDownload = async () => {
    setIsCapturing(true);

    try {
      const SCALE = 2; // retina quality
      const W = CARD_WIDTH;
      const FONT = `-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif`;

      // Load QR image (always CORS-accessible from the API CDN).
      // Logo is skipped for Firebase Storage URLs (CORS limitation — see drawInitialsPlaceholder).
      const [qrResult, logoResult] = await Promise.allSettled([
        loadCanvasImage(qrUrl),
        npoImage ? loadCanvasImage(npoImage) : Promise.reject('no-logo')
      ]);

      const qrImg = qrResult.status === 'fulfilled' ? qrResult.value : null;
      const logoImg = logoResult.status === 'fulfilled' ? logoResult.value : null;

      // ── Calculate total card height ────────────────────────────────────────
      const NPO_NAME_H = 24;
      const LINK_ROW_H = 38;
      const BRANDING_H = 20;
      const TOTAL_H =
        PAD + // top
        LOGO_SIZE +
        10 + // logo gap
        NPO_NAME_H +
        18 + // gap before QR
        QR_SIZE +
        16 + // gap before link
        LINK_ROW_H +
        14 + // gap before branding
        BRANDING_H +
        PAD; // bottom

      const canvas = document.createElement('canvas');
      canvas.width = W * SCALE;
      canvas.height = TOTAL_H * SCALE;

      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('No canvas context');

      ctx.scale(SCALE, SCALE);

      // ── Background ─────────────────────────────────────────────────────────
      ctx.fillStyle = '#ffffff';
      roundedRect(ctx, 0, 0, W, TOTAL_H, 20);
      ctx.fill();

      ctx.strokeStyle = '#F0F0F0';
      ctx.lineWidth = 1;
      roundedRect(ctx, 0.5, 0.5, W - 1, TOTAL_H - 1, 20);
      ctx.stroke();

      let y = PAD;

      // ── Logo ───────────────────────────────────────────────────────────────
      const logoCX = W / 2;
      const logoCY = y + LOGO_SIZE / 2;
      const logoR = LOGO_SIZE / 2;

      if (logoImg) {
        drawCircleImage(ctx, logoImg, logoCX, logoCY, logoR);
      } else {
        // Draw styled initials badge — looks intentional, not broken.
        drawInitialsPlaceholder(ctx, npoName, logoCX, logoCY, logoR, FONT);
      }

      y += LOGO_SIZE + 10;

      // ── NPO Name ───────────────────────────────────────────────────────────
      ctx.fillStyle = '#000000';
      ctx.font = `700 18px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';

      // Truncate if needed
      const maxNameW = W - PAD * 2;
      let displayName = npoName;

      while (ctx.measureText(displayName).width > maxNameW && displayName.length > 1) {
        displayName = displayName.slice(0, -1);
      }

      if (displayName.length < npoName.length) displayName += '…';
      ctx.fillText(displayName, W / 2, y);

      y += NPO_NAME_H + 18;

      // ── QR Container ───────────────────────────────────────────────────────
      const qrX = (W - QR_SIZE) / 2;
      const INNER_PAD = 12;

      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#F0F0F0';
      ctx.lineWidth = 1;
      roundedRect(ctx, qrX, y, QR_SIZE, QR_SIZE, 12);
      ctx.fill();
      ctx.stroke();

      if (qrImg) {
        ctx.drawImage(qrImg, qrX + INNER_PAD, y + INNER_PAD, QR_SIZE - INNER_PAD * 2, QR_SIZE - INNER_PAD * 2);
      }

      y += QR_SIZE + 16;

      // ── Link Row ───────────────────────────────────────────────────────────
      const linkPadH = 9;
      const linkPadV = 14;

      ctx.fillStyle = '#F8F8F8';
      roundedRect(ctx, PAD, y, W - PAD * 2, LINK_ROW_H, 10);
      ctx.fill();

      ctx.fillStyle = '#666666';
      ctx.font = `400 13px ${FONT}`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';

      const iconW = 20;
      const maxLinkW = W - PAD * 2 - linkPadV * 2 - iconW - 6;
      let displayLink = shortLink;

      while (ctx.measureText(displayLink).width > maxLinkW && displayLink.length > 1) {
        displayLink = displayLink.slice(0, -1);
      }

      if (displayLink.length < shortLink.length) displayLink += '…';
      ctx.fillText(displayLink, PAD + linkPadV, y + LINK_ROW_H / 2);

      y += LINK_ROW_H + 14;

      // ── Branding ───────────────────────────────────────────────────────────
      ctx.fillStyle = '#000000';
      ctx.font = `600 14px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(i18n.t('header.bs'), W / 2, y);

      // ── Export ─────────────────────────────────────────────────────────────
      canvas.toBlob((blob) => {
        if (!blob) return;

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${npoName.replace(/\s+/g, '_')}_QR.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 'image/png');
    } catch (error) {
      console.error('QR download error:', error);
      Toast.show({
        type: 'error',
        text1: i18n.t('end_flow.error_message') || 'Error',
        text2: i18n.t('mix.failed_download_qr'),
        position: 'bottom'
      });
    } finally {
      setIsCapturing(false);
    }
  };

  // ── Render (pure RN — no HTML tags, no TypeScript errors) ─────────────────
  return (
    <View style={styles.wrapper}>
      <View style={styles.card}>
        <View style={styles.header}>
          {npoImage ? (
            <Image source={{ uri: npoImage }} style={styles.logo} />
          ) : (
            <View style={styles.logoPlaceholder}>
              <Ionicons name="business" size={26} color="#ccc" />
            </View>
          )}

          <Text style={styles.npoName} fontWeight="700" numberOfLines={2}>
            {npoName}
          </Text>
        </View>

        <View style={styles.qrContainer}>
          <Image source={{ uri: qrUrl }} style={styles.qrImage} resizeMode="contain" />
        </View>

        <View style={styles.footer}>
          <TouchableOpacity style={styles.linkRow} onPress={onCopyLink}>
            <Text style={styles.linkText} numberOfLines={1}>
              {shortLink}
            </Text>

            <Ionicons name="copy-outline" size={16} color={colors.linkText} />
          </TouchableOpacity>

          <View style={styles.branding}>
            <Text style={styles.brandingText} fontWeight="600">
              {i18n.t('header.bs')}
            </Text>
          </View>
        </View>
      </View>

      <BrandedButton style={styles.downloadBtn} onPress={onDownload} loading={isCapturing}>
        <Ionicons name="download-outline" size={20} color="white" />
        {i18n.t('profile.download_qr')}
      </BrandedButton>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    alignItems: 'center'
  },

  card: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: PAD,
    width: '100%',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#F0F0F0'
  },

  header: {
    alignItems: 'center',
    width: '100%',
    rowGap: 10,
    marginBottom: 16
  },

  logo: {
    width: LOGO_SIZE,
    height: LOGO_SIZE,
    borderRadius: LOGO_SIZE / 2
  },

  logoPlaceholder: {
    width: LOGO_SIZE,
    height: LOGO_SIZE,
    borderRadius: LOGO_SIZE / 2,
    backgroundColor: '#F5F5F5',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#EEE'
  },

  npoName: {
    fontSize: 17,
    color: 'black',
    textAlign: 'center',
    lineHeight: 22
  },

  qrContainer: {
    backgroundColor: 'white',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0F0F0',
    width: QR_SIZE,
    height: QR_SIZE,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 14
  },

  qrImage: {
    width: '100%',
    height: '100%'
  },

  footer: {
    width: '100%',
    alignItems: 'center',
    rowGap: 12
  },

  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8F8F8',
    paddingVertical: 9,
    paddingHorizontal: 14,
    borderRadius: 10,
    width: '100%',
    columnGap: 8
  },

  linkText: {
    fontSize: 12,
    color: '#666',
    flex: 1,
    lineHeight: 17
  },

  branding: {
    marginTop: 4
  },

  brandingText: {
    fontSize: 13,
    color: 'black',
    letterSpacing: 0.5,
    lineHeight: 18
  },

  downloadBtn: {
    marginTop: 16,
    width: '100%'
  }
});

export default ShareQrCardWeb;
